import { spriteRotationForWorldAngle } from '../game/art-orientation';

/**
 * Which way a hull is pointing, and how fast it gets there.
 *
 * A duel's ship has two orientations and they are independent: the HULL faces
 * where it is travelling, and the CANNON faces the cursor. The player asked for
 * exactly that after playing a build where the hull held one heading forever.
 *
 * ## Why this is presentation and lives here
 *
 * The room does not have a hull angle and must not grow one. Nothing about it
 * changes where a ship is, what it collides with, where a shot is born or who
 * wins - the simulation moves an integer point through integer geometry and is
 * blind to which way the art is turned. Inventing a server field for a picture
 * would put a number in the authoritative snapshot that the authority never
 * reads.
 *
 * So the heading is derived, on each client, from something both clients
 * already have:
 *
 * - for the LOCAL ship, the movement intent the player is holding, because it
 *   is instant and it is what they are asking for;
 * - for the OPPONENT, the displacement between authoritative samples, because
 *   it is what they actually did. Never their aim - that is the cannon's, and
 *   a ship strafing while shooting behind itself would otherwise be drawn
 *   flying backwards.
 */

/** The scale a fixed-point aim vector arrives in. */
const NO_MOVEMENT = { x: 0, y: 0 } as const;

/**
 * How fast a hull turns, in radians per second.
 *
 * A full half-turn - the worst case, W to S - takes 250 ms at this rate, which
 * is long enough to read as a ship coming about and short enough that the nose
 * is pointing the right way before the ship has crossed its own length.
 *
 * A RATE rather than a fraction per frame. A fraction is a different rotation
 * at every refresh rate: the same key held for the same time would leave a
 * 144 Hz client further round than a 30 Hz one, which is the whole class of bug
 * the prediction work already had to remove once.
 */
export const PVP_HULL_TURN_RATE = Math.PI * 4;

/**
 * The heading a movement intent asks for, or `null` for none.
 *
 * `null` covers both hands off the keys and opposite keys held together: the
 * axes cancel to zero and the ship keeps whatever heading it had. A ship that
 * snapped to some default the moment a player stopped would swing its nose
 * every time they let go.
 *
 * Screen axes, so `-y` is up: W is up, S is down, and the four diagonals fall
 * exactly on the odd eighths.
 */
export function pvpHullTargetFromMovement(mx: number, my: number): number | null {
  const x = Math.sign(mx);
  const y = Math.sign(my);
  if (x === 0 && y === 0) {
    return null;
  }
  return Math.atan2(y, x);
}

/**
 * The heading a move between two authoritative samples implies, or `null`.
 *
 * The threshold is one thousandth of a world unit, which is the domain's own
 * resolution: below it the ship did not move, and asking `atan2` about rounding
 * noise would make a stationary opponent's nose jitter.
 */
export function pvpHullTargetFromMotion(
  from: { readonly x: number; readonly y: number } | null,
  to: { readonly x: number; readonly y: number },
): number | null {
  if (!from) {
    return null;
  }
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === NO_MOVEMENT.x && dy === NO_MOVEMENT.y) {
    return null;
  }
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
    return null;
  }
  return Math.atan2(dy, dx);
}

/** The signed difference between two angles, in `(-PI, PI]`. */
export function pvpAngleDelta(from: number, to: number): number {
  const raw = (to - from) % (Math.PI * 2);
  if (raw > Math.PI) {
    return raw - Math.PI * 2;
  }
  if (raw <= -Math.PI) {
    return raw + Math.PI * 2;
  }
  return raw;
}

/**
 * Turns `current` towards `target` by whatever the elapsed time allows.
 *
 * The shortest way round, and never past the target: a turn that overshoots
 * would oscillate, and one that took the long way would swing the nose through
 * the direction the ship is coming from.
 *
 * Elapsed time is clamped so a stalled tab cannot deliver one enormous frame
 * and spin the hull several times on the way back - the same reason the
 * prediction clamps its own integration.
 */
export function pvpTurnHull(
  current: number,
  target: number | null,
  elapsedMs: number,
  rate: number = PVP_HULL_TURN_RATE,
): number {
  if (target === null || !Number.isFinite(target)) {
    return current;
  }
  const delta = pvpAngleDelta(current, target);
  if (delta === 0) {
    return current;
  }
  const allowed = (Math.max(0, Math.min(elapsedMs, 250)) / 1_000) * rate;
  if (allowed >= Math.abs(delta)) {
    return target;
  }
  return current + Math.sign(delta) * allowed;
}

/**
 * The rotation to draw a hull at, given the heading it is facing.
 *
 * The art points UP, which is `-PI/2` in world terms, so every world angle
 * gains a quarter turn on the way to the sprite. `HuntScene` applies exactly
 * the same offset to the same asset; a duel that used a different one would
 * draw the same ship facing somewhere else.
 */
export function pvpHullRotation(heading: number): number {
  return spriteRotationForWorldAngle(heading);
}
